import type { ComponentRef, OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  reflectComponentType,
  ViewChild,
  ViewContainerRef,
  output,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { PushPipe } from '@ngrx/component';
import { BehaviorSubject, combineLatest, merge, Subject, switchMap, take, takeUntil } from 'rxjs';

import { filterBoolean, trackByProp } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { Select2Item } from '@core/user-interface-legacy';
import {
  AlwaysOpenStrategyDirective,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownWidth,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  OverlayPosition,
  RtTooltip,
  Select2Component,
  Select2TriggerButtonComponent,
  Select2TriggerDirective,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type {
  ViewFilterConditionDef,
  ViewFilterDef,
  ViewFilterInputDef,
  FilterControl,
} from '@core/views-types';

import type { InputTypeComponent } from '../../../advanced-filters/filter/types';
import { injectInputServices } from '../../../advanced-filters/tokens';
import type { InputComponentContext, InputService } from '../../../input-types/types';

@Component({
  selector: 'rt-views-quick-filter',
  imports: [
    ButtonComponent,
    IconComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    ButtonDropdownIndicatorDirective,
    DropdownComponent,
    DropdownHeaderComponent,
    Select2Component,
    Select2TriggerButtonComponent,
    ReactiveFormsModule,
    PushPipe,
    IconButtonComponent,
    HeadingComponent,
    Select2TriggerDirective,
    AlwaysOpenStrategyDirective,
    TranslatePipe,
    TruncateComponent,
    RtTooltip,
  ],
  templateUrl: './quick-filter.component.html',
  styleUrls: ['./quick-filter.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickFilterComponent implements OnChanges {
  private readonly cdRef = inject(ChangeDetectorRef);

  @Input() public dataSourceId: string;
  @Input() public filterUuid: string;
  @Input() public labelValueText: string;
  @Input() public filterDef: ViewFilterDef;
  @Input() public filterInputDefs: ViewFilterInputDef[];
  @Input() public filterControl: FilterControl;
  @Input() public filterState: unknown[];

  public clear = output();
  public filterStateChanges = output<unknown[]>();

  public DropdownWidth = DropdownWidth;
  public OverlayPosition = OverlayPosition;
  public conditionOptions: Select2Item[];
  public conditionIdMap: Record<string, ViewFilterConditionDef>;
  public showBackButton$ = new Subject<boolean>();
  public isOnlySelect = false;
  public isNoInputFilter = false;
  public trackById = trackByProp<{ id: string }>({ prop: 'id' });

  private filterControl$ = new BehaviorSubject<FilterControl | null>(null);
  private inputServices = injectInputServices();
  private _inputsRef: ViewContainerRef;
  private destroyInputs$ = new Subject<void>();
  @ViewChild('inputsRef', { read: ViewContainerRef, static: false })
  private set inputsRef(value: ViewContainerRef) {
    this._inputsRef = value;
    if (value) {
      this.createInputComponents();
    } else {
      this.disposeInputComponents();
    }
  }
  private get inputsRef() {
    return this._inputsRef;
  }
  private inputComponentsRef: Array<ComponentRef<InputTypeComponent<any, unknown, any>>> = [];
  private untilDestroyed = injectUntilDestroyed();

  public get filterLabel() {
    if (!this.filterControl.valid) return { name: this.filterDef.name, value: '' };

    const conditionLabel = this.conditionIdMap[this.filterControl.value.condition!].label;
    return {
      name: `${this.filterDef.name} ${conditionLabel.toLowerCase()}:`,
      value: this.labelValueText.toLowerCase(),
    };
  }

  public get filterTooltip() {
    const filterLabel = this.filterLabel;
    return `${filterLabel.name} ${filterLabel.value}`;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.filterDef && this.filterDef) {
      const { conditions } = this.filterDef;
      this.isNoInputFilter = !conditions.flatMap(condition => condition.inputs).length;
      this.conditionOptions = conditions.map(condition => ({
        label: condition.label,
        value: condition.id,
      }));
      this.conditionIdMap = Object.fromEntries(
        conditions.map(condition => [condition.id, condition]),
      );
    }

    if (changes.filterInputDefs) {
      const [firstInput] = this.filterInputDefs;
      this.isOnlySelect =
        this.filterInputDefs.length === 1 &&
        (firstInput.type === 'select' || firstInput.type === 'multi_select');
      this.createInputComponents();
    }

    if (changes.filterControl) {
      this.filterControl$.next(this.filterControl);
    }
  }

  public ngOnInit() {
    this.filterControl$
      .pipe(
        filterBoolean,
        switchMap(control => merge(control.valueChanges, control.statusChanges)),
        this.untilDestroyed,
      )
      .subscribe(() => {
        this.cdRef.detectChanges();
      });
  }

  public onClear(event: MouseEvent) {
    event.stopPropagation();
    this.clear.emit();
  }

  private createInputComponents() {
    this.disposeInputComponents();
    if (!this.inputsRef || !this.filterInputDefs.length) return;

    const filterEvents: InputComponentContext['filterEvents'] = new Subject();
    this.inputComponentsRef = this.filterInputDefs.map(input => {
      const inputService = this.inputServices[input.type];
      const componentRef = inputService.createInputComponent(this.inputsRef, this.destroyInputs$, {
        dataSourceId: this.dataSourceId,
        filterDef: this.filterDef,
        inputDef: input,
        inputs: this.filterInputDefs,
        origin: 'quick',
        uuid: this.filterUuid,
        filterEvents,
      });
      this.destroyInputs$.pipe(take(1)).subscribe(() => {
        filterEvents.complete();
        componentRef.destroy();
      });
      return componentRef;
    });
    this.inputComponentsRef.forEach((_, index) => this.updateOptionInputs(index));

    combineLatest(this.inputComponentsRef.map(componentRef => componentRef.instance.state$))
      .pipe(takeUntil(this.destroyInputs$))
      .subscribe(filterState => {
        this.filterStateChanges.emit(filterState);
      });
  }

  private updateOptionInputs(index: number) {
    if (!this.inputComponentsRef[index]) return;

    const input = this.filterInputDefs[index];
    const inputService = this.inputServices[input.type];
    const componentRef = this.inputComponentsRef[index];
    const cmpMirror = reflectComponentType(componentRef.componentType)!;
    const allInputs = {
      inputControl: this.filterControl.controls.value.controls[index],
      state: this.filterState[index],
      showBackButton$: this.showBackButton$,
    } satisfies Parameters<InputService['updateInputs']>[1];
    const supportedInputs = Object.fromEntries(
      Object.entries(allInputs).filter(([name]) =>
        cmpMirror.inputs.some(input => input.templateName === name),
      ),
    );
    inputService.updateInputs(this.inputComponentsRef[index], supportedInputs);
  }

  private disposeInputComponents() {
    this.destroyInputs$.next();
    this.inputsRef?.clear();
    this.inputComponentsRef = [];
  }
}
